import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import {
    Warning, ClockCounterClockwise, XCircle, PaperPlaneTilt,
    ArrowRight, Bell, Envelope, DotsThreeVertical, ArrowUp, XCircle as XCircleIcon, PencilSimple,
    UploadSimple,
} from "@phosphor-icons/react";
import BulkInviteModal from "@/components/BulkInviteModal";
import { useAuth } from "@/context/AuthContext";
import { useFeatures } from "@/lib/features";

/**
 * NudgeQueue — mini-dashboard listing candidates who need immediate recruiter
 * attention. Three tabs, one badge count in the sidebar.
 *
 * Backend endpoints:
 *   GET /api/nudge-queue/summary
 *   GET /api/nudge-queue/list?bucket=awaiting|stuck|auto_rejected
 */
const BUCKETS = [
    { key: "awaiting",       label: "Awaiting registration",   icon: Envelope,       tone: "amber" },
    { key: "stuck",          label: "Stuck > 7 days",          icon: ClockCounterClockwise, tone: "cobalt" },
    { key: "auto_rejected",  label: "Auto-closed (24 h)",      icon: XCircle,        tone: "rose" },
];

const TONE_CLS = {
    amber: {
        border: "border-l-[#F59E0B]", chip: "bg-amber-100 text-amber-900",
        headline: "text-amber-900", accent: "bg-amber-50/50",
    },
    cobalt: {
        border: "border-l-cobalt", chip: "bg-cobalt/10 text-cobalt",
        headline: "text-ink", accent: "bg-cobalt/5",
    },
    rose: {
        border: "border-l-[#DC2626]", chip: "bg-rose-100 text-rose-900",
        headline: "text-rose-900", accent: "bg-rose-50/50",
    },
};

export default function NudgeQueue() {
    const { user } = useAuth();
    const { isEnabled } = useFeatures();
    const [tab, setTab] = useState("awaiting");
    const [summary, setSummary] = useState(null);
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [busyId, setBusyId] = useState(null);
    const [bulkOpen, setBulkOpen] = useState(false);

    const loadSummary = useCallback(() => {
        api.get("/nudge-queue/summary")
            .then((r) => setSummary(r.data))
            .catch(() => setSummary(null));
    }, []);

    const loadRows = useCallback(() => {
        setLoading(true);
        api.get("/nudge-queue/list", { params: { bucket: tab, limit: 100 } })
            .then((r) => setRows(r.data?.items || []))
            .catch(() => setRows([]))
            .finally(() => setLoading(false));
    }, [tab]);

    useEffect(() => { loadSummary(); }, [loadSummary]);
    useEffect(() => { loadRows(); }, [loadRows]);

    const nudge = async (cid) => {
        setBusyId(cid);
        try {
            const r = await api.post(`/candidates/${cid}/nudge-registration`);
            toast.success(`Reminder sent (${r.data?.drip_state?.sent_count || "?"}/5).`);
            loadRows(); loadSummary();
        } catch (ex) {
            toast.error(ex?.response?.data?.detail || "Nudge failed.");
        } finally {
            setBusyId(null);
        }
    };

    const NEXT_STAGE = { Applied: "Screening", Screening: "Interview", Interview: "Offer", Offer: "Hired" };
    const moveNext = async (row) => {
        const next = NEXT_STAGE[row.stage];
        if (!next) return;
        setBusyId(row.id);
        try {
            await api.patch(`/candidates/${row.id}/stage`, { stage: next });
            toast.success(`${row.name || "Candidate"} moved to ${next}.`);
            loadRows(); loadSummary();
        } catch (ex) {
            toast.error(ex?.response?.data?.detail || "Stage move failed.");
        } finally { setBusyId(null); }
    };
    const rejectNow = async (row) => {
        if (!window.confirm(`Reject ${row.name || row.email}? They will be moved to the Rejected stage.`)) return;
        setBusyId(row.id);
        try {
            await api.patch(`/candidates/${row.id}/stage`, { stage: "Rejected" });
            toast.success(`${row.name || "Candidate"} rejected — pipeline updated.`);
            loadRows(); loadSummary();
        } catch (ex) {
            toast.error(ex?.response?.data?.detail || "Reject failed.");
        } finally { setBusyId(null); }
    };
    const addNote = async (row) => {
        const note = window.prompt(`Add an internal note for ${row.name || row.email}:`);
        if (!note || !note.trim()) return;
        setBusyId(row.id);
        try {
            await api.post(`/candidates/${row.id}/notes`, { text: note.trim() });
            toast.success("Note saved.");
        } catch (ex) {
            toast.error(ex?.response?.data?.detail || "Couldn't save note.");
        } finally { setBusyId(null); }
    };

    const stats = useMemo(() => summary || {}, [summary]);
    const activeTone = TONE_CLS[BUCKETS.find((b) => b.key === tab)?.tone] || TONE_CLS.cobalt;

    return (
        <div className="px-4 sm:px-8 py-6 sm:py-10 max-w-[1200px] mx-auto" data-testid="nudge-queue-page">
            {/* Header */}
            <div className="mb-8 sm:mb-10 flex items-start justify-between flex-wrap gap-4">
                <div>
                    <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#A3A3A3] mb-2">Recruiter focus</p>
                    <h1 className="font-heading text-4xl sm:text-5xl font-black tracking-tighter text-ink">
                        Nudge queue.
                    </h1>
                    <p className="text-sm text-[#525252] mt-3 max-w-lg">
                        Candidates who need action right now — waiting on registration, stuck in a stage, or already auto-closed. Fewer surprises, fewer no-responses.
                    </p>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                    {isEnabled("resume_parse_bulk") ? (
                        <button
                            onClick={() => setBulkOpen(true)}
                            data-testid="nudge-queue-bulk-invite-btn"
                            className="text-[10px] font-black uppercase tracking-[0.2em] border border-cobalt text-cobalt hover:bg-cobalt hover:text-white px-3 py-2 inline-flex items-center gap-1.5 transition-colors"
                        >
                            <UploadSimple size={11} weight="bold" /> Bulk-invite CSV
                        </button>
                    ) : (
                        <Link
                            to="/billing"
                            data-testid="nudge-queue-bulk-invite-locked"
                            title="Bulk invite requires Pro plan"
                            className="text-[10px] font-black uppercase tracking-[0.2em] border border-[#E5E5E5] text-[#A3A3A3] hover:border-splash hover:text-ink px-3 py-2 inline-flex items-center gap-1.5 transition-colors"
                        >
                            <UploadSimple size={11} weight="bold" /> Bulk-invite CSV — upgrade
                        </Link>
                    )}
                    <div className="flex items-center gap-2 text-[10px] font-black uppercase tracking-[0.2em] bg-ink text-white px-3 py-2">
                        <Bell size={12} weight="fill" />
                        <span className="tabular-nums">{stats.badge_count ?? "—"}</span>
                        <span>pending</span>
                    </div>
                </div>
            </div>

            {/* Bulk-invite modal (opens from header) */}
            <BulkInviteModal
                open={bulkOpen}
                onClose={() => setBulkOpen(false)}
                onDone={() => { loadRows(); loadSummary(); }}
            />

            {/* Summary tiles */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-8">
                <SummaryTile
                    tone="amber" icon={Envelope}
                    label="Awaiting registration"
                    total={stats.awaiting_registration?.total}
                    hint={stats.awaiting_registration?.critical
                        ? `${stats.awaiting_registration.critical} on the final reminder`
                        : "All within drip window"}
                    onClick={() => setTab("awaiting")}
                    active={tab === "awaiting"}
                    data-testid="tile-awaiting"
                />
                <SummaryTile
                    tone="cobalt" icon={ClockCounterClockwise}
                    label={`Stuck > ${stats.stuck_stage?.threshold_days ?? 7} days`}
                    total={stats.stuck_stage?.total}
                    hint={stats.stuck_stage?.over_14_days
                        ? `${stats.stuck_stage.over_14_days} untouched 14 d+`
                        : "Recent stalls only"}
                    onClick={() => setTab("stuck")}
                    active={tab === "stuck"}
                    data-testid="tile-stuck"
                />
                <SummaryTile
                    tone="rose" icon={XCircle}
                    label="Auto-closed today"
                    total={stats.awaiting_registration?.auto_rejected_24h}
                    hint="No response after 5 reminders"
                    onClick={() => setTab("auto_rejected")}
                    active={tab === "auto_rejected"}
                    data-testid="tile-auto-rejected"
                />
            </div>

            {/* Tab bar */}
            <div className="border-b border-[#E5E5E5] mb-4 flex flex-wrap gap-1">
                {BUCKETS.map((b) => {
                    const Icon = b.icon;
                    const isActive = tab === b.key;
                    return (
                        <button
                            key={b.key}
                            data-testid={`nudge-tab-${b.key}`}
                            onClick={() => setTab(b.key)}
                            className={`px-4 py-2.5 text-[11px] font-black uppercase tracking-[0.15em] flex items-center gap-2 border-b-2 -mb-px transition-colors ${
                                isActive
                                    ? "border-ink text-ink"
                                    : "border-transparent text-[#A3A3A3] hover:text-[#525252]"
                            }`}
                        >
                            <Icon size={13} weight="bold" />
                            {b.label}
                        </button>
                    );
                })}
            </div>

            {/* Rows */}
            {loading ? (
                <div className="p-16 text-center text-[#A3A3A3] text-sm">Loading…</div>
            ) : rows.length === 0 ? (
                <div className="border border-[#E5E5E5] p-12 text-center max-w-[560px] mx-auto">
                    <PaperPlaneTilt size={32} weight="bold" className="mx-auto text-[#A3A3A3] mb-3" />
                    <p className="font-heading text-2xl font-black tracking-tight text-ink mb-2">
                        Inbox zero.
                    </p>
                    <p className="text-sm text-[#525252] mb-5">
                        {tab === "awaiting" && "No candidates waiting on registration right now."}
                        {tab === "stuck" && "Every candidate in every stage was touched in the last 7 days."}
                        {tab === "auto_rejected" && "No auto-closures in the last 24 hours."}
                    </p>
                    <div className="border-t border-[#E5E5E5] pt-5 text-left">
                        <p className="text-[10px] font-black uppercase tracking-[0.2em] text-[#A3A3A3] mb-2.5 text-center">
                            How to keep it this way
                        </p>
                        <ul className="space-y-2 text-xs text-[#525252]">
                            {tab === "awaiting" && (
                                <>
                                    <li className="flex gap-2"><span className="text-cobalt font-black shrink-0">&rarr;</span><span><span className="font-bold text-ink">Ask before adding.</span> Confirm the candidate has 2 min to sign up &mdash; the first reminder fires immediately after add.</span></li>
                                    <li className="flex gap-2"><span className="text-cobalt font-black shrink-0">&rarr;</span><span><span className="font-bold text-ink">Use &ldquo;Send reminder now&rdquo;</span> from the candidate panel when you&rsquo;re on a call &mdash; it keeps momentum without waiting for the 48 h tick.</span></li>
                                    <li className="flex gap-2"><span className="text-cobalt font-black shrink-0">&rarr;</span><span><span className="font-bold text-ink">Add via the portal link.</span> Share the requirement&rsquo;s public link &mdash; candidates who apply directly skip this queue entirely.</span></li>
                                </>
                            )}
                            {tab === "stuck" && (
                                <>
                                    <li className="flex gap-2"><span className="text-cobalt font-black shrink-0">&rarr;</span><span><span className="font-bold text-ink">Batch-review Fridays.</span> Open Candidates, filter by stage, and clear anything &gt; 3 days old before the weekend.</span></li>
                                    <li className="flex gap-2"><span className="text-cobalt font-black shrink-0">&rarr;</span><span><span className="font-bold text-ink">Schedule the next step.</span> The moment a candidate reaches Interview, book the call &mdash; otherwise stalls compound.</span></li>
                                    <li className="flex gap-2"><span className="text-cobalt font-black shrink-0">&rarr;</span><span><span className="font-bold text-ink">Reject early, not never.</span> A polite decline within 5 days protects your candidate NPS and keeps this queue empty.</span></li>
                                </>
                            )}
                            {tab === "auto_rejected" && (
                                <>
                                    <li className="flex gap-2"><span className="text-cobalt font-black shrink-0">&rarr;</span><span><span className="font-bold text-ink">Warm intro before drip.</span> Send a WhatsApp / call before adding to portal so the first reminder isn&rsquo;t cold-inbox noise.</span></li>
                                    <li className="flex gap-2"><span className="text-cobalt font-black shrink-0">&rarr;</span><span><span className="font-bold text-ink">Verify the email at source.</span> Typos in email = 5 wasted reminders. Copy-paste from the CV, not memory.</span></li>
                                    <li className="flex gap-2"><span className="text-cobalt font-black shrink-0">&rarr;</span><span><span className="font-bold text-ink">Consider a follow-up job.</span> Auto-closed doesn&rsquo;t mean bad-fit &mdash; reach out again on the next matching req.</span></li>
                                </>
                            )}
                        </ul>
                    </div>
                </div>
            ) : (
                <div className={`border border-[#E5E5E5] ${activeTone.accent}`} data-testid="nudge-queue-list">
                    {rows.map((r) => (
                        <NudgeRow
                            key={r.id}
                            row={r}
                            bucket={tab}
                            tone={BUCKETS.find((b) => b.key === tab)?.tone}
                            busy={busyId === r.id}
                            onNudge={() => nudge(r.id)}
                            onMoveNext={() => moveNext(r)}
                            onReject={() => rejectNow(r)}
                            onAddNote={() => addNote(r)}
                            canNudge={user?.role !== "hiring_manager"}
                            canWrite={["admin", "recruiter", "hiring_manager", "superadmin"].includes(user?.role)}
                        />
                    ))}
                </div>
            )}
        </div>
    );
}


function SummaryTile({ tone, icon: Icon, label, total, hint, onClick, active, ...rest }) {
    const t = TONE_CLS[tone];
    return (
        <button
            type="button"
            onClick={onClick}
            {...rest}
            className={`text-left border-l-4 ${t.border} border-y border-r p-4 transition-colors ${
                active ? "border-y-ink border-r-ink bg-white" : "border-y-[#E5E5E5] border-r-[#E5E5E5] bg-white hover:bg-[#FAFAFA]"
            }`}
        >
            <div className="flex items-start justify-between mb-2">
                <p className="text-[10px] font-black uppercase tracking-[0.2em] text-[#525252]">{label}</p>
                <Icon size={16} weight="bold" className="text-[#A3A3A3]" />
            </div>
            <p className={`font-heading font-black text-4xl tracking-tighter tabular-nums ${t.headline}`}>
                {total ?? "—"}
            </p>
            <p className="text-[11px] text-[#525252] mt-1">{hint}</p>
        </button>
    );
}


function NudgeRow({ row, bucket, tone, busy, onNudge, onMoveNext, onReject, onAddNote, canNudge, canWrite }) {
    const t = TONE_CLS[tone] || TONE_CLS.cobalt;
    const drip = row.drip || {};
    const [menuOpen, setMenuOpen] = useState(false);
    const menuRef = React.useRef(null);
    const NEXT_LABEL = { Applied: "Screening", Screening: "Interview", Interview: "Offer", Offer: "Hired" };
    const nextStage = NEXT_LABEL[row.stage];

    // Close menu on outside click
    useEffect(() => {
        if (!menuOpen) return;
        const handler = (e) => {
            if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
        };
        document.addEventListener("mousedown", handler);
        return () => document.removeEventListener("mousedown", handler);
    }, [menuOpen]);

    return (
        <div
            data-testid={`nudge-row-${row.id}`}
            className="border-b last:border-b-0 border-[#E5E5E5] px-4 py-3 flex flex-col sm:flex-row sm:items-center gap-3 hover:bg-white transition-colors"
        >
            <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <p className="font-bold text-ink truncate max-w-full">{row.name || "(unnamed)"}</p>
                    <span className="text-[10px] font-mono text-[#A3A3A3] truncate max-w-full">{row.email}</span>
                </div>
                <p className="text-[11px] text-[#525252] truncate">
                    <span className="font-semibold text-ink">{row.job_title || "—"}</span>
                    {row.client_name ? <> · {row.client_name}</> : null}
                    <> · stage </>
                    <span className={`inline-block px-1.5 py-0.5 text-[9px] font-black uppercase tracking-wider ${t.chip}`}>
                        {row.stage}
                    </span>
                </p>
            </div>

            {/* Bucket-specific status + Actions — grouped so they wrap together on mobile */}
            <div className="flex items-center justify-between sm:justify-end gap-3 shrink-0 sm:w-auto w-full">
            {bucket === "awaiting" && (
                <div className="text-left sm:text-right shrink-0">
                    <p className="text-[10px] font-black uppercase tracking-wider text-[#525252]">Reminders</p>
                    <p className={`font-heading font-black text-2xl tabular-nums tracking-tight ${drip.sent_count >= drip.max_sends - 1 ? "text-[#DC2626]" : "text-ink"}`}>
                        {drip.sent_count || 0}<span className="text-sm text-[#A3A3A3]">/{drip.max_sends || 5}</span>
                    </p>
                </div>
            )}
            {bucket === "stuck" && (
                <div className="text-left sm:text-right shrink-0">
                    <p className="text-[10px] font-black uppercase tracking-wider text-[#525252]">Untouched</p>
                    <p className="font-heading font-black text-2xl tabular-nums tracking-tight text-ink">
                        {row.days_since_update ?? "—"}<span className="text-sm text-[#A3A3A3]">d</span>
                    </p>
                </div>
            )}
            {bucket === "auto_rejected" && (
                <div className="text-left sm:text-right shrink-0">
                    <p className="text-[10px] font-black uppercase tracking-wider text-[#DC2626]">Auto-closed</p>
                    <p className="text-[11px] text-[#525252]">
                        {drip.completed_at ? new Date(drip.completed_at).toLocaleString("en-IN", { day:"numeric", month:"short", hour:"2-digit", minute:"2-digit" }) : "—"}
                    </p>
                </div>
            )}

            {/* Actions */}
            <div className="flex items-center gap-2 shrink-0">
                {bucket === "awaiting" && canNudge && (
                    <button
                        type="button"
                        data-testid={`nudge-row-${row.id}-nudge`}
                        onClick={onNudge}
                        disabled={busy}
                        className="bg-ink hover:bg-cobalt disabled:opacity-50 disabled:cursor-not-allowed text-white text-[10px] font-black uppercase tracking-wider px-2.5 py-1.5 inline-flex items-center gap-1"
                    >
                        {busy ? "Sending…" : <>Nudge <PaperPlaneTilt size={11} weight="bold" /></>}
                    </button>
                )}
                {canWrite && (
                    <div className="relative" ref={menuRef}>
                        <button
                            type="button"
                            data-testid={`nudge-row-${row.id}-actions`}
                            onClick={() => setMenuOpen((v) => !v)}
                            disabled={busy}
                            aria-label="Quick actions"
                            className="border border-[#E5E5E5] hover:border-ink text-[#525252] hover:text-ink p-1.5 disabled:opacity-50"
                        >
                            <DotsThreeVertical size={14} weight="bold" />
                        </button>
                        {menuOpen && (
                            <div
                                data-testid={`nudge-row-${row.id}-menu`}
                                className="absolute right-0 top-full mt-1 z-20 min-w-[180px] bg-white border border-[#E5E5E5] shadow-lg"
                            >
                                {nextStage && bucket !== "awaiting" && (
                                    <button
                                        type="button"
                                        data-testid={`nudge-row-${row.id}-move-next`}
                                        onClick={() => { setMenuOpen(false); onMoveNext(); }}
                                        className="w-full text-left flex items-center gap-2 px-3 py-2 text-xs font-bold hover:bg-cobalt/5 hover:text-cobalt transition-colors"
                                    >
                                        <ArrowUp size={12} weight="bold" /> Move to {nextStage}
                                    </button>
                                )}
                                <button
                                    type="button"
                                    data-testid={`nudge-row-${row.id}-add-note`}
                                    onClick={() => { setMenuOpen(false); onAddNote(); }}
                                    className="w-full text-left flex items-center gap-2 px-3 py-2 text-xs font-bold hover:bg-ink/5 transition-colors"
                                >
                                    <PencilSimple size={12} weight="bold" /> Add note
                                </button>
                                {bucket !== "auto_rejected" && row.stage !== "Rejected" && (
                                    <button
                                        type="button"
                                        data-testid={`nudge-row-${row.id}-reject`}
                                        onClick={() => { setMenuOpen(false); onReject(); }}
                                        className="w-full text-left flex items-center gap-2 px-3 py-2 text-xs font-bold text-[#DC2626] hover:bg-rose-50 transition-colors border-t border-[#F5F5F5]"
                                    >
                                        <XCircleIcon size={12} weight="bold" /> Reject
                                    </button>
                                )}
                            </div>
                        )}
                    </div>
                )}
                <Link
                    to={`/candidates?open=${row.id}`}
                    data-testid={`nudge-row-${row.id}-open`}
                    className="border border-[#E5E5E5] hover:border-ink text-[10px] font-black uppercase tracking-wider px-2.5 py-1.5 text-[#525252] hover:text-ink inline-flex items-center gap-1"
                >
                    Open <ArrowRight size={11} weight="bold" />
                </Link>
            </div>
            </div>
        </div>
    );
}
